import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { saveEntitlementBudget, type BudgetUnit } from "../../api/entitlementBudgets";
import { ToggleSwitch } from "../shared/ToggleSwitch";
import { useToast } from "../toast/toastContext";
import { formatWholeUsd, isCapexInherited, isHardStopInherited, parseBudgetAmount } from "./budgetFormat";
import styles from "./Budgets.module.css";

/**
 * A unit's budget editor: the monthly amount (blank clears it), the unit's hard stop, whether its spend is Capex, and, for a holder of
 * `budgets.backdate`, an "applies since" date. Saving is one call; the outcome is a toast, and `onSaved` reloads the budgets. A hard stop inherited from
 * an ancestor is shown on and disabled, with the unit it comes from named (SPEC R-11a), and so is a Capex mark: Capex is inherited, so
 * only a unit with no Capex ancestor can change its own. Remount it (a `key`) when the unit's stored
 * budget changes, so the draft starts from the saved values.
 *
 * @param props.unit - The unit being edited.
 * @param props.editable - Whether the caller may change it; when false the budget is shown read-only.
 * @param props.canBackdate - Whether the caller holds `budgets.backdate`, which shows the "applies since" date.
 * @param props.onSaved - Called after a successful save.
 */
export function BudgetEditor({ unit, editable, canBackdate, onSaved }: {
  unit: BudgetUnit;
  editable: boolean;
  canBackdate: boolean;
  onSaved: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const ids = useId();
  const [amount, setAmount] = useState(unit.explicitBudgetUsd === null ? "" : String(unit.explicitBudgetUsd));
  const [hardStop, setHardStop] = useState(unit.ownHardStop);
  const [capex, setCapex] = useState(unit.ownCapex);
  const [appliesSince, setAppliesSince] = useState("");
  const [saving, setSaving] = useState(false);
  const inherited = isHardStopInherited(unit);
  const inheritedFrom = unit.hardStopSourceName ?? t(`budgets.kind.${unit.hardStopSourceKind ?? "Group"}`);
  const capexInherited = isCapexInherited(unit);
  const capexInheritedFrom = unit.capexSourceName ?? t(`budgets.kind.${unit.capexSourceKind ?? "Group"}`);
  const amountId = `${ids}-amount`;
  const hardStopId = `${ids}-hard-stop`;
  const capexId = `${ids}-capex`;
  const sinceId = `${ids}-since`;

  if (!editable) {
    return <BudgetSummary unit={unit} inheritedFrom={inheritedFrom} capexInheritedFrom={capexInheritedFrom} />;
  }

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = parseBudgetAmount(amount);
    if (parsed === undefined) {
      toast.show(t("budgets.invalidAmount"), "error");
      return;
    }
    setSaving(true);
    // An inherited hard stop is not this unit's to change, so the unit's own stored value is sent back unchanged.
    const result = await saveEntitlementBudget(unit.kind, unit.id, {
      amountUsd: parsed,
      hardStop: inherited ? unit.ownHardStop : hardStop,
      // Likewise an inherited Capex mark is sent back unchanged.
      capex: capexInherited ? unit.ownCapex : capex,
      effectiveFrom: canBackdate && appliesSince !== "" ? appliesSince : null,
    });
    setSaving(false);
    if (result.kind === "ok") {
      toast.show(t("budgets.saved"), "success");
      onSaved();
      return;
    }
    toast.show(result.kind === "denied" ? t("budgets.denied") : result.message, "error");
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    // noValidate: the amount is checked here, so a refusal is a toast rather than the browser's own bubble.
    <form className={styles.editorForm} noValidate onSubmit={(event) => void save(event)} aria-label={t("budgets.editorLabel", { name: unit.name })}>
      <div className={styles.editorRow}>
        <label htmlFor={amountId}>{t("budgets.amountLabel")}</label>
        <div className={styles.currencyInput}>
          <span className={styles.currencySymbol} aria-hidden="true">$</span>
          <input
            id={amountId}
            type="number"
            min="0"
            max="9999999"
            step="1"
            value={amount}
            disabled={saving}
            placeholder={unit.explicitBudgetUsd === null && unit.impliedBudgetUsd !== null ? String(unit.impliedBudgetUsd) : undefined}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>
      </div>
      <p className={styles.editorNote}>
        {unit.explicitBudgetUsd === null
          ? unit.impliedBudgetUsd === null ? t("budgets.noBudget") : t("budgets.impliedNote", { amount: formatWholeUsd(unit.impliedBudgetUsd) })
          : unit.allocatedUsd > 0 ? t("budgets.allocatedNote", { allocated: formatWholeUsd(unit.allocatedUsd), budget: formatWholeUsd(unit.explicitBudgetUsd) }) : t("budgets.explicitNote", { budget: formatWholeUsd(unit.explicitBudgetUsd) })}
      </p>
      <div className={styles.switchRow}>
        <span id={hardStopId}>{t("budgets.hardStop.label")}</span>
        <ToggleSwitch checked={inherited ? true : hardStop} onChange={setHardStop} labelledBy={hardStopId} disabled={inherited || saving} />
      </div>
      {inherited && <p className={styles.editorNote}>{t("budgets.hardStop.inherited", { name: inheritedFrom })}</p>}
      <div className={styles.switchRow}>
        <span id={capexId}>{t("budgets.capex.label")}</span>
        <ToggleSwitch checked={capexInherited ? true : capex} onChange={setCapex} labelledBy={capexId} disabled={capexInherited || saving} />
      </div>
      <p className={styles.editorNote}>{capexInherited ? t("budgets.capex.inherited", { name: capexInheritedFrom }) : t("budgets.capex.hint")}</p>
      {canBackdate && (
        <div className={styles.editorRow}>
          <label htmlFor={sinceId}>{t("budgets.appliesSince")}</label>
          <input id={sinceId} type="date" max={today} value={appliesSince} disabled={saving} onChange={(event) => setAppliesSince(event.target.value)} />
          <p className={styles.editorNote}>{t("budgets.appliesSinceHint")}</p>
        </div>
      )}
      <div className={styles.editorActions}>
        <button type="submit" className="button button-primary" disabled={saving}>{t("budgets.save")}</button>
      </div>
    </form>
  );
}

/** A unit's budget as text, for a caller who may not change it. */
function BudgetSummary({ unit, inheritedFrom, capexInheritedFrom }: { unit: BudgetUnit; inheritedFrom: string; capexInheritedFrom: string }): React.JSX.Element {
  const { t } = useTranslation();
  const inherited = isHardStopInherited(unit);
  const capexText = unit.effectiveCapex ? (isCapexInherited(unit) ? t("budgets.capex.inherited", { name: capexInheritedFrom }) : t("budgets.capex.on")) : t("budgets.capex.off");
  if (unit.budgetUsd === null) {
    return (
      <div className={styles.editor}>
        <p className={styles.editorNote}>{t("budgets.noBudget")}</p>
        <p className={styles.editorNote}>{capexText}</p>
      </div>
    );
  }
  return (
    <div className={styles.editor}>
      <p>
        {unit.explicitBudgetUsd === null
          ? t("budgets.impliedValue", { amount: formatWholeUsd(unit.budgetUsd) })
          : unit.allocatedUsd > 0 ? t("budgets.allocatedNote", { allocated: formatWholeUsd(unit.allocatedUsd), budget: formatWholeUsd(unit.budgetUsd) }) : t("budgets.explicitNote", { budget: formatWholeUsd(unit.budgetUsd) })}
      </p>
      <p className={styles.editorNote}>{unit.effectiveHardStop ? (inherited ? t("budgets.hardStop.inherited", { name: inheritedFrom }) : t("budgets.hardStop.on")) : t("budgets.hardStop.off")}</p>
      <p className={styles.editorNote}>{capexText}</p>
    </div>
  );
}
